import { z } from 'zod'
import { PageQuery } from './pagination.ts'
import { ISODate, Uuid, Version, blankAsAbsent, moneyField } from './primitives.ts'

/**
 * ══ HỢP ĐỒNG API — ĐỀ NGHỊ THANH TOÁN (F18) ═══════════════════════════════════════════════════
 * Nguồn sự thật duy nhất cho `/api/v1/dntt/*`.
 */

/** Một tờ hóa đơn kèm phiếu ĐNTT. */
export const RequestInvoiceBody = z.object({
  no: z.string().max(100),
  date: z.union([ISODate, z.literal('')]),
  preTax: z.number().nonnegative().optional(),
  vatRate: z.number().min(0).max(100).optional(),
  vatAmount: z.number().nonnegative().optional(),
  amount: z.number().nonnegative().optional(),
  note: z.string().max(500).optional(),
  objCode: z.string().max(50).optional(),
  objName: z.string().max(200).optional(),
})

export const CreateRequestBody = z.object({
  /** Lập TỪ chứng từ mua (đường chính). Bỏ trống = phiếu nhập tay, khi đó phải có tiền và tên. */
  apInvoiceId: blankAsAbsent(Uuid),
  docDate: blankAsAbsent(ISODate),
  vendorId: blankAsAbsent(Uuid),
  vendorName: blankAsAbsent(z.string()),
  description: blankAsAbsent(z.string()),
  amount: blankAsAbsent(moneyField('Số tiền')),
  category: blankAsAbsent(z.string()),
  expenseItem: blankAsAbsent(z.string()),
  deadline: blankAsAbsent(ISODate),
  note: blankAsAbsent(z.string()),
  /* ⭐ Form Tạo ĐNTT đầy đủ (`0105`, `Approvals.tsx:5457`). */
  approverId: blankAsAbsent(Uuid),
  hasInvoice: blankAsAbsent(z.enum(['yes', 'no'])),
  invoices: z.array(RequestInvoiceBody).max(50).default([]),
  quantity: blankAsAbsent(z.string().regex(/^\d+(\.\d+)?$/, 'Số lượng không hợp lệ.')),
  unitPrice: blankAsAbsent(moneyField('Đơn giá')),
  ckContent: blankAsAbsent(z.string().max(500)),
  payeeCode: blankAsAbsent(z.string().max(50)),
  payeeTax: blankAsAbsent(z.string().max(50)),
  payeeAccount: blankAsAbsent(z.string().max(50)),
  payeeBank: blankAsAbsent(z.string().max(200)),
  payeeHolder: blankAsAbsent(z.string().max(200)),
  handover: z.boolean().default(false),
  submit: z.boolean().default(false),
})

/** ✏️ Sửa phiếu — cùng form tạo. Vắng khoá = không đổi; `null` = xoá giá trị. */
export const UpdateRequestBody = z.object({
  version: Version,
  description: blankAsAbsent(z.string()),
  category: blankAsAbsent(z.string()),
  expenseItem: blankAsAbsent(z.string()),
  amount: blankAsAbsent(moneyField('Số tiền')),
  quantity: blankAsAbsent(z.string().nullable()),
  unitPrice: blankAsAbsent(z.string().nullable()),
  vendorId: blankAsAbsent(Uuid.nullable()),
  vendorName: blankAsAbsent(z.string()),
  payeeCode: blankAsAbsent(z.string().nullable()),
  payeeTax: blankAsAbsent(z.string().nullable()),
  payeeAccount: blankAsAbsent(z.string().nullable()),
  payeeBank: blankAsAbsent(z.string().nullable()),
  payeeHolder: blankAsAbsent(z.string().nullable()),
  deadline: blankAsAbsent(ISODate),
  note: blankAsAbsent(z.string().nullable()),
  approverId: blankAsAbsent(Uuid.nullable()),
  hasInvoice: blankAsAbsent(z.enum(['yes', 'no'])),
  invoices: z.array(RequestInvoiceBody).max(50).optional(),
  ckContent: blankAsAbsent(z.string().nullable()),
  handover: z.boolean().optional(),
})
export type UpdateRequestBodyT = z.infer<typeof UpdateRequestBody>

export const RequestReasonBody = z.object({ version: Version, reason: z.string().default('') })
export type RequestReasonBodyT = z.infer<typeof RequestReasonBody>

export const RequestPayeeBody = z.object({
  version: Version,
  payeeAccount: z.string().max(50).nullable(),
  payeeBank: z.string().max(200).nullable(),
  payeeHolder: z.string().max(200).nullable().optional(),
})
export type RequestPayeeBodyT = z.infer<typeof RequestPayeeBody>

export const RequestCkBody = z.object({ version: Version, ckContent: z.string().max(500).nullable() })
export type RequestCkBodyT = z.infer<typeof RequestCkBody>

export const RequestHandoverBody = z.object({ version: Version, action: z.enum(['hand', 'undo', 'receive']) })
export type RequestHandoverBodyT = z.infer<typeof RequestHandoverBody>

/**
 * 💬 Ghi chú vào Hoạt động. Bản cũ gửi được CHỈ ẢNH / TỆP không kèm chữ (`ChatFeed`, `Approvals.tsx:901-1001`) ⇒
 * `message` được rỗng khi có `attachmentIds` — tệp đã tải lên trước qua `/attachments` (ngăn `chat`).
 */
export const RequestCommentBody = z.object({
  message: z.string().max(2000),
  attachmentIds: z.array(Uuid).max(50).optional(),
}).refine((b) => b.message.trim() !== '' || (b.attachmentIds ?? []).length > 0, { message: 'Ghi chú trống.', path: ['message'] })
export type RequestCommentBodyT = z.infer<typeof RequestCommentBody>

export const RequestStepBody = z.object({ version: Version })

/* ══ 📒 ĐỊNH KHOẢN → CHỨNG TỪ AMIS của phiếu nhập tay (`0106`) ══════════════════════════════════════════════════════ */

const Acc = z.string().trim().min(1, 'Thiếu tài khoản.').max(20)

export const PostingLineBody = z.object({
  description: z.string().max(500),
  debitAcc: Acc,
  creditAcc: Acc,
  /** Sau thuế, đồng. */
  afterTax: z.number().finite(),
  preTax: z.number().finite().nonnegative().optional(),
  vatRate: z.number().min(0).max(100).optional(),
  expenseItem: z.string().max(100).optional(),
  invoiceNo: z.string().max(100).optional(),
  invoiceDate: z.union([ISODate, z.literal('')]).optional(),
  objCode: z.string().max(50).optional(),
  objName: z.string().max(200).optional(),
})

export const CreatePostingBody = z.object({
  version: Version,
  lines: z.array(PostingLineBody).min(1).max(100),
  mgmtOnly: z.boolean(),
  otherJournal: z.boolean().default(false),
  invoiceReceipt: z.enum(['attached', 'not_attached', 'none']),
  accountingDate: blankAsAbsent(ISODate),
  dueDate: blankAsAbsent(ISODate),
  adjust: z.object({ description: z.string().max(500), debitAcc: Acc, creditAcc: Acc, expenseItem: z.string().max(100).optional() }).optional(),
})
export type CreatePostingBodyT = z.infer<typeof CreatePostingBody>

export const EditPostingDocBody = z.object({
  version: Version,
  partnerName: z.string().trim().min(1).max(200).optional(),
  partnerTax: z.string().max(50).nullable().optional(),
  lines: z.array(z.object({
    lineNo: z.number().int().min(1),
    description: z.string().max(500).optional(),
    expenseItem: z.string().max(100).nullable().optional(),
    debitAcc: Acc.optional(),
    creditAcc: Acc.optional(),
  })).max(200).optional(),
})
export type EditPostingDocBodyT = z.infer<typeof EditPostingDocBody>

export const PostingDocParams = z.object({ id: Uuid, docId: Uuid })

/** Căn cứ chính sách hoa hồng của một chứng từ mua — để form Lập ĐNTT chi phí đơn hàng hiện trước khi tạo. */
export const PayBasisQuery = z.object({ apInvoiceId: Uuid })
export type PayBasisQueryT = z.infer<typeof PayBasisQuery>

export const RejectRequestBody = z.object({
  version: Version,
  reason: z.string().min(1, 'Phải ghi lý do từ chối — người lập cần biết phải sửa gì.'),
})

export const ScheduleBody = z.object({
  version: Version,
  payDate: ISODate,
})

export const MarkPaidBody = z.object({
  version: Version,
  payDate: ISODate,
  amount: moneyField('Số tiền chi'),
  /**
   * ⛔ KHÔNG CÒN `offset` (bù trừ) — người dùng chốt 17/10 theo bản cũ (cách chi ĐNTT bản cũ chỉ có tiền mặt / chuyển khoản —
   *   `types.ts:2628` `PaymentMethod`, `ketoan/dnttPay.ts:176-200`). Bù trừ ở đây trừ công nợ PHẢI TRẢ mà phía PHẢI THU không có phiếu đối trừ nào ⇒ hai sổ lệch nhau.
   *   Bù trừ công nợ đi qua phiếu Đối trừ AR↔AP (/ar ▸ Đối trừ). Phiếu cũ đã ghi `offset` vẫn ĐỌC được (`pay_method`).
   */
  method: z.enum(['bank', 'cash', 'loan'], {
    message: 'Cách chi chỉ là chuyển khoản / tiền mặt. Bù trừ công nợ lập phiếu Đối trừ ở Công nợ phải thu ▸ Đối trừ.',
  }).optional(),
  note: blankAsAbsent(z.string()),
  /**
   * ⭐ SỔ TIỀN CỦA KHOẢN CHI (`0121`). Chi chuyển khoản / tiền mặt phải NÓI tiền ra từ đâu — ĐÚNG MỘT trong hai:
   *   · `accountId` — tài khoản ngân hàng (sinh Báo Nợ) hoặc quỹ (sinh phiếu chi TM);
   *   · `bankTxnId` + `bankTxnVersion` — khớp một dòng CHI sao kê đã nhập sẵn, bằng đúng số tiền.
   * Khoản vay (`loan`) không đi qua tài khoản của mình — không nhận hai ô này.
   */
  accountId: blankAsAbsent(Uuid),
  bankTxnId: blankAsAbsent(Uuid),
  bankTxnVersion: z.number().int().positive().optional(),
  /**
   * ⭐ "🏦 ĐÃ CHI QUA NGÂN HÀNG" KIỂU BẢN CŨ (`Approvals.tsx:4736-4748`, anh chốt 24/09/2026): chỉ cần số UNC,
   * KHÔNG chọn tài khoản, KHÔNG sinh dòng sổ phụ. Có `unc` mà bỏ trống cả `accountId` lẫn `bankTxnId` là đi
   * đường này; khoản chi hiện "chưa có dòng sổ phụ" để kế toán ghi bổ sung sau (`/ghi-so-phu`).
   */
  unc: blankAsAbsent(z.string()),
})

/** 📒 GHI SỔ PHỤ BỔ SUNG cho khoản đã chi TRƯỚC `0121` (hoặc bị sót) — chọn tài khoản / quỹ tiền ra. */
export const LedgerBackfillBody = z.object({
  accountId: blankAsAbsent(Uuid),
})

export const ListRequestQuery = PageQuery.extend({
  status: blankAsAbsent(z.enum(['pending', 'approved', 'scheduled', 'paid', 'rejected'])),
  vendorId: blankAsAbsent(Uuid),
  /** `'true'` = chỉ phiếu ĐÃ TRÌNH (hàng chờ người duyệt) · `'false'` = chỉ phiếu còn nháp. */
  submitted: blankAsAbsent(z.enum(['true', 'false'])),
})

/* ══ GIẢI NGÂN (F19) ═════════════════════════════════════════════════════════════════════════ */

/**
 * ⚠ `loan` = chi bằng KHOẢN VAY (`0065`). Năm ô cuối chỉ có nghĩa với nó; luật "vay thì phải có
 *   bên cho vay, và KHÔNG được mang tài khoản chi" chốt ở `@rs/pay` để câu từ chối là tiếng Việt.
 */
export const CreateDocBody = z.object({
  kind: z.enum(['cash', 'bank', 'loan']),
  requestIds: z.array(Uuid).min(1, 'Chưa chọn phiếu đề nghị thanh toán nào.'),
  amount: moneyField('Số tiền chi'),
  docDate: blankAsAbsent(ISODate),
  bankAccountId: blankAsAbsent(Uuid),
  content: blankAsAbsent(z.string()),
  note: blankAsAbsent(z.string()),
  lenderCode: blankAsAbsent(z.string()),
  lenderName: blankAsAbsent(z.string()),
  loanRef: blankAsAbsent(z.string()),
  debitAccount: blankAsAbsent(z.string()),
  creditAccount: blankAsAbsent(z.string()),
})

export const CancelDocBody = z.object({
  version: Version,
  reason: z.string().min(1, 'Phải ghi lý do huỷ chứng từ chi.'),
})

export const ListDocQuery = PageQuery.extend({
  kind: blankAsAbsent(z.enum(['cash', 'bank', 'loan'])),
  status: blankAsAbsent(z.enum(['issued', 'cancelled'])),
})

export const PayableQuery = z.object({ vendorId: blankAsAbsent(Uuid) })

export const RequestIdParams = z.object({ id: Uuid })

export type CreateRequestBodyT = z.infer<typeof CreateRequestBody>
export type MarkPaidBodyT = z.infer<typeof MarkPaidBody>
export type LedgerBackfillBodyT = z.infer<typeof LedgerBackfillBody>
export type ListRequestQueryT = z.infer<typeof ListRequestQuery>
export type CreateDocBodyT = z.infer<typeof CreateDocBody>
export type ListDocQueryT = z.infer<typeof ListDocQuery>
